import { type UVInsertConfig, isUvInsertUserConfigurableText } from "../../models/uv-inserts"
import type { TemplateIds } from "../../models/uv-inserts/template-ids"

/**
 * Cart properties for a UV insert line item.
 * String values are Line Item Properties; File values are image uploads.
 */
export type UvInsertCartLineItemProperties = Record<string, string | File>

/**
 * A fully-built cart line item for a UV insert, ready for Shopify's /cart/add.js.
 */
export type UvInsertCartLineItem = {
  /** Numeric Shopify variant ID. */
  id: number
  quantity: 1
  properties: UvInsertCartLineItemProperties
}

/**
 * Builds a Shopify cart line item for a UV insert entirely from MVVM-parsed data.
 * Pure function — no window access, no side effects.
 *
 * Template IDs, LIP keys, and variant IDs all come from {@link resolveUvInserts} output,
 * so this never reads window.giftWrapData or window.uvInsertsConfig directly.
 *
 * @returns The ready-to-add cart item, or null if the config has no valid theme variant.
 */
export function buildUvInsertCartLineItem(options: {
  /** The resolved UV insert config (from resolveUvInserts). */
  config: UVInsertConfig
  /** Index into config.themes selecting the active design variant. */
  themeIndex: number
  /** Template IDs from resolveUvInserts — keyed by numeric variant ID. */
  templateIds: TemplateIds
  /** Text values keyed by lineItemPropertyKey (e.g. UVFrontText). */
  textByKey: Record<string, string>
  /** Image files keyed by lineItemPropertyKey (e.g. UVFrontImage). */
  imageByKey: Record<string, File | null>
  /** Unique gift-wrap key linking this insert to the parent product. */
  giftWrapKey: string
  /** Title of the parent product being gift-wrapped. */
  parentTitle: string
  /** Optional vendor for fulfilment routing. */
  vendor?: string | null
}): UvInsertCartLineItem | null {
  const {
    config,
    themeIndex,
    templateIds,
    textByKey,
    imageByKey,
    giftWrapKey,
    parentTitle,
    vendor,
  } = options

  const safeIndex = Math.min(Math.max(themeIndex, 0), config.themes.length - 1)
  const variant = config.themes[safeIndex]
  if (!variant) return null

  // Variant IDs from Liquid JSON are numbers; TemplateIds is keyed by coerced number.
  const variantId = Number(variant.id)
  if (!variantId || Number.isNaN(variantId)) return null

  const templateId = templateIds[variantId] ?? null

  const properties: UvInsertCartLineItemProperties = {
    _giftwrap_key: giftWrapKey,
    _giftwrap_parent_title: parentTitle,
  }

  if (typeof templateId === "string" && templateId.trim() !== "") {
    properties._template_id = templateId
  }

  if (typeof vendor === "string" && vendor.trim() !== "") {
    properties._vendorRoutingMatch = vendor
  }

  // Map text and image inputs using lineItemPropertyKey as the LIP key.
  // Since the key IS the cart property name (e.g. UVFrontText), this is an identity mapping.
  // TEXT_LIST has no lineItemPropertyKey of its own — it contributes no properties here; the
  // host writes its per-row keys into textByKey directly, and other TEXT elements pick them up.
  for (const face of config.faceElements) {
    for (const el of face.elementConfig) {
      if (el.type === "TEXT") {
        if (!isUvInsertUserConfigurableText(el) || !el.lineItemPropertyKey) continue
        const value = textByKey[el.lineItemPropertyKey] ?? ""
        if (value.trim() !== "") {
          properties[el.lineItemPropertyKey] = value
        }
      } else if (el.type === "IMAGE_UPLOAD") {
        if (!el.lineItemPropertyKey) continue
        const file = imageByKey[el.lineItemPropertyKey] ?? null
        if (file instanceof File) {
          properties[el.lineItemPropertyKey] = file
        }
      }
    }
  }

  return { id: variantId, quantity: 1, properties }
}
